<template>
  <form
    :class="formClass"
    novalidate
    class="contact-form"
    @submit.prevent="handleSubmit"
  >
    <div class="contact-form-wrap">
      <div class="contact-form__inline-wrap contact-form__inline-wrap--two-cols">
        <div class="contact-form__field">
          <text-input
            :disabled="isLoading"
            type="text"
            name="full-name"
            placeholder="Your full name"
            required
            v-model="fullName"
            :show-error="!!fullNameFieldError"
            :error-msg="fullNameFieldError"
          />
        </div>
        <div class="contact-form__field">
          <text-input
            :disabled="shouldDisableReferralInput"
            type="text"
            name="referralCode"
            placeholder="Referral code"
            v-model="referralCode"
          />
        </div>
      </div>
      <div class="contact-form__inline-wrap contact-form__inline-wrap--two-cols">
        <div class="contact-form__field">
          <text-input
            :disabled="isLoading"
            type="text"
            name="email"
            placeholder="Your email"
            required
            v-model="email"
            :show-error="!!emailFieldError"
            :error-msg="emailFieldError"
          />
        </div>
        <div class="contact-form__field">
          <text-input
            :disabled="isLoading"
            type="text"
            name="phone"
            placeholder="Phone"
            pattern="[0-9() \-+]"
            v-model="phone"
          />
        </div>
      </div>
      <div class="contact-form__inline-wrap contact-form__inline-wrap--two-cols">
        <div class="contact-form__field">
          <text-input
            :disabled="isLoading"
            type="text"
            name="monthlyOrders"
            placeholder="Current units/orders processing per month"
            v-model="monthlyOrders"
          />
        </div>
        <div class="contact-form__field">
          <text-input
            :disabled="isLoading"
            type="text"
            name="monthlyRevenue"
            placeholder="Current monthly revenue from product sales"
            v-model="monthlyRevenue"
          />
        </div>
      </div>
      <div class="contact-form__field">
        <text-area-input
          :disabled="isLoading"
          class="contact-form__field__body"
          name="body"
          placeholder="Tell us about your product or business"
          required
          v-model="body"
          :show-error="!!bodyFieldError"
          :error-msg="bodyFieldError"
        />
      </div>
      <div class="contact-form__button-wrap">
        <ui-button
          type="submit"
          :disabled="isLoading"
        >
          Send
        </ui-button>
      </div>
    </div>

    <spinner
      width="40px"
      height="40px"
      class="contact-form__spinner"
    />
    <div
      v-show="shouldShowSuccess"
      class="contact-form__success"
    >
      <div class="contact-form__success__content">
        <success-icon />
        <p class="contact-form__success__text">
          Thank you!<br>
          We’ll be in contact with you shortly.
        </p>
      </div>
    </div>
  </form>
</template>

<script>
import Spinner from 'components/ui/Spinner'
import TextInput from './components/forms/TextInput'
import TextAreaInput from './components/forms/TextAreaInput'
import SuccessIcon from 'static/assets/icons/SuccessIcon.svg'
import UiButton from './components/ui/UiButton'
import * as api from '@/lib/api'

export default {
  name: 'ContactForm',
  components: {
    Spinner,
    TextInput,
    TextAreaInput,
    SuccessIcon,
    UiButton,
  },
  data () {
    return {
      isLoading: false,
      errors: null,
      shouldShowSuccess: false,
      fullName: '',
      referralCode: '',
      email: '',
      phone: '',
      monthlyOrders: '',
      monthlyRevenue: '',
      body: '',
      emailIsDirty: false,
    }
  },
  computed: {
    formClass () {
      return {
        'contact-form--loading': this.isLoading,
      }
    },
    fullNameFieldError () {
      const error = this.errors && this.errors.find(e => e.field === 'fullName')
      if (error) {
        return error.message
      }
    },
    emailFieldError () {
      const error = this.errors && this.errors.find(e => e.field === 'email')
      if (error) {
        return error.message
      }
    },
    bodyFieldError () {
      const error = this.errors && this.errors.find(e => e.field === 'body')
      if (error) {
        return error.message
      }
    },
    shouldDisableReferralInput () {
      return this.isLoading || !!this.refQueryParam
    },
    refQueryParam () {
      return location.search.replace(/[&?]ref=([a-zA-Z0-9]+)/, '$1')
    },
  },
  watch: {
    email (email) {
      if (email) this.emailIsDirty = true
    },
    isLoading (isLoading) {
      this.$emit('is-loading', isLoading)
    },
  },
  methods: {
    handleSubmit () {
      if (this.shouldShowSuccess) return

      const { fullName, referralCode, email, phone, monthlyOrders, monthlyRevenue, body } = this
      if (fullName && email && body) {
        this.emailIsDirty = false
        this.errors = null
        this.isLoading = true

        const callback = resp => {
          if (resp.status === 'fail') {
            this.errors = resp.errors
          } else {
            this.shouldShowSuccess = true

            window.fbq('track', 'FormSubmission', {
              content_name: 'Form sent',
              value: 'Email sent',
            })
          }
          this.isLoading = false
        }

        gtag('event', 'conversion', {'send_to': 'AW-801274654/vKYwCN3Cw5IBEJ72if4C'})
        api.post('/contact-us',
          { fullName, referralCode, email, phone, monthlyOrders, monthlyRevenue, body },
          callback
        )
      }
    },
  },
  created () {
    if (this.refQueryParam) {
      this.referralCode = this.refQueryParam
    }
  }
}
</script>

<style lang="sass" scoped>
@import '~styles/shared'

.contact-form
  max-width: 924px
  margin-left: auto
  margin-right: auto
  padding: 0 7px
  position: relative

  @include tablet
    position: static

.contact-form__spinner.loader
  display: none
  position: absolute
  left: 50%
  top: 50%
  margin-left: -25px
  margin-top: -25px

.contact-form-content-wrap
  position: relative

.contact-form--loading
  .contact-form__spinner.loader
    display: block

  .contact-form-wrap
    visibility: hidden

.contact-form__field
  padding: 9px 0

.contact-form__inline-wrap--two-cols
  @media (min-width: $mobile-wide)
    display: flex
    justify-content: space-between

    .contact-form__field
      width: calc(50% - 7px)

      @include desktop
        width: calc(50% - 9px)

.contact-form__button-wrap
  text-align: center
  margin-top: 14px

  @include desktop
    margin-top: 4px

.ui-button
  /deep/
    .button
      min-width: 163px
      padding-top: 12px
      padding-bottom: 13px
      letter-spacing: 0.8px
      font-size: 17px

.contact-form__success
  background: #fff
  text-align: center
  display: flex
  flex-direction: column
  justify-content: center
  position: absolute
  top: 0
  right: 0
  bottom: 0
  left: 0
  z-index: 2

  .success-icon
    width: 56px
    margin-bottom: 9px

.contact-form__success__text
  color: #525f7f
  font-weight: 600
  letter-spacing: 0.2px
</style>



// WEBPACK FOOTER //
// src/ContactForm.vue